import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isSalesRole } from '../lib/sales-access';
import {
  FileText,
  Plus,
  DollarSign,
  Clock,
  CheckCircle,
  Filter,
  Loader2,
  Send,
  ChevronRight,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap, unwrapArray } from '../lib/api-envelope';
import { formatCalendarDate, formatInstantDay } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { fmtCurrency } from '../lib/display-labels';
import { ndtQuoteCurrency, ndtTenantQuoteCurrency } from '../lib/ndt-quotes';
import { useTenantCurrency } from '../contexts/LocaleContext';
import { sortCompare, makeToggleSort } from '../components/SortTh';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../lib/load-failure';
import { quoteListStatus } from '../lib/ew-quotes';

/**
 * The filter chips, each paired with the dashboard key that counts it.
 *
 * NO NEW QUERY, AND NO CLIENT-SIDE TALLY. `/quotes/summary/dashboard` is
 * already fetched below for the stat strip, and it already returns all six of
 * these as `COUNT(*) FILTER (WHERE status = …)` plus a plain `COUNT(*)` —
 * verified in quotes.routes.ts, not assumed. Counting the loaded rows instead
 * would be wrong twice over: it is the second answer to a question the API
 * already answers, and it is not even computable, because `quotes` is the
 * status-FILTERED list. On the Draft tab a client-side tally would report
 * `Sent 0` while sent quotes existed.
 *
 * `total_count` is the API's own total rather than the sum of the other five.
 * Summing them would agree today — `public.quote_status` has exactly these
 * five members — and it would still be this repo recomputing a figure the API
 * returned, which is how two answers to one question get started.
 *
 * THE COUNTS ARE COMPANY-WIDE and do NOT follow the client select beside the
 * chips, exactly like the stat strip above them; the dashboard endpoint takes
 * no customer parameter. Narrowing them to the selected client is a query
 * change, which this re-skin may not make.
 */
const STATUS_TABS = [
  { key: 'all', label: 'All', countKey: 'total_count' },
  { key: 'draft', label: 'Draft', countKey: 'draft_count' },
  { key: 'sent', label: 'Sent', countKey: 'sent_count' },
  { key: 'accepted', label: 'Accepted', countKey: 'accepted_count' },
  { key: 'declined', label: 'Declined', countKey: 'declined_count' },
  { key: 'expired', label: 'Expired', countKey: 'expired_count' },
] as const;

/**
 * The status pill: a SATURATED FILL with dark ink, one hue per state.
 *
 * EXHAUSTIVE, which is checkable rather than hopeful: `public.quote_status` is a
 * Postgres enum of exactly these five members, so there is no sixth value the
 * API can send. That is what CLAUDE.md asks a label map to be — the failure it
 * warns about is a map with a hole and a fallback that guesses, which reads on
 * screen as a data bug.
 *
 * WHY IT IS LOCAL, AND WHY IT IS NOT `LedgerStatusBadge`. That component is the
 * handoff vocabulary — six tones, each a 10% wash of a `--fi-*` hue behind that
 * hue's own text — and four other ledgers in that directory still render it.
 * The washes are the thing this change is replacing, so re-skinning the shared
 * one would repaint all four to serve a register none of them is. Same call
 * this file already makes for `SortHead` a few lines down, and for the same
 * reason: only the paint is local. A shared saturated pill built from one call
 * site is an abstraction guessed rather than observed, and the second re-skinned
 * page is when it stops being a guess.
 *
 * WHY THE HUES ARE `--fi-signal-*` AND NOT `--fi-*`. A pill carries its own
 * fill, so its ink is measured against that fill and not against the page. The
 * `--fi-*` hues invert across the theme switch — `--fi-blue` is a pale sky in
 * the dark half and #1d4ed8 in the light one — so one constant dark ink on a
 * fill taken from that token would be 6.95:1 in dark mode and unreadable in
 * light. (The utility that fills with it is deliberately NOT spelled here:
 * Tailwind's scanner reads a comment as source, and quoting it in this
 * paragraph minted the rule with no call site. The built-CSS diff caught it.)
 * The signal hues do not answer the switch, so one ink is correct in both.
 * Measured
 * against `--fi-signal-ink`: draft 11.18, sent 6.95, accepted 10.14, declined
 * 6.57, expired 11.91. The floor is 4.5.
 *
 * The edge is a constant black at 20% rather than a hue: on the dark register
 * these fills sit at 6.1-11.1 against the surface and need no help, while on
 * the light one they sit at 1.5-2.7 and would otherwise float. One rule that
 * darkens whatever it is drawn on does both jobs without a sixth entry per row.
 */
interface QuoteStatusSkin {
  label: string;
  fill: string;
}

const QUOTE_STATUS: Record<string, QuoteStatusSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-amber' },
  sent: { label: 'Sent', fill: 'bg-fi-signal-blue' },
  accepted: { label: 'Accepted', fill: 'bg-fi-signal-green' },
  declined: { label: 'Declined', fill: 'bg-fi-signal-red' },
  expired: { label: 'Expired', fill: 'bg-fi-signal-slate' },
  // Not a quote_status label: an Earthworks quote the customer has opened by
  // link (EW-2). The API says so (customer_viewed_at); lib/ew-quotes decides
  // only which of the two to draw.
  viewed: { label: 'Viewed', fill: 'bg-fi-signal-blue' },
};

function QuoteStatusBadge({ status }: { status: QuoteStatusSkin }) {
  return (
    <span
      className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${status.fill}`}
    >
      {status.label}
    </span>
  );
}

/**
 * The 4px rail on the left edge of a table row, keyed on status.
 *
 * EXHAUSTIVE over the same five-member Postgres enum QUOTE_STATUS covers, so
 * there is no reachable fallback. The one below is for a value the enum cannot
 * produce, and it paints NO colour rather than guessing one: a rail is a claim
 * about which state a row is in, and a wrong claim in a colour is the silent
 * kind of wrong this repo keeps finding. It keeps the 4px width so an unknown
 * row still lines up with its neighbours.
 *
 * FOUR OF THE FIVE NOW AGREE WITH THE BADGE, which they did not before: the
 * badges were washes of a different vocabulary and `draft` was grey there while
 * it has always been amber here. They are separate maps because they answer
 * separate questions — the rail separates the five states along the edge of a
 * scrolling register, the badge names one — and collapsing them would tie a
 * 4px edge treatment to a pill's fill for no reason beyond their agreeing
 * today.
 *
 * `expired` is the one that still differs: orange on the edge, a neutral slate
 * on the pill. That is the design's call and it is the right way round — an
 * expired quote is a dead row and its pill should not shout, while the edge of
 * a long list still has to tell it apart from the amber drafts above it.
 */
const STATUS_RAIL: Record<string, string> = {
  draft: 'border-l-fi-amber',
  sent: 'border-l-fi-blue',
  accepted: 'border-l-fi-green',
  declined: 'border-l-fi-red',
  expired: 'border-l-fi-orange',
};

/**
 * @see fmtCurrency — absence renders '—', not '$0.00'. `currency` is set for an
 * NDT quote or tenant only (lib/ndt-quotes.ts); every other caller passes null
 * and keeps the bare '$'.
 */
const formatMoney = (amount: number | string | null | undefined, currency: string | null = null) => fmtCurrency(amount, currency);

/** created_at is an instant: the TENANT's day it fell on, or an em dash. */
function formatDate(dateStr: string, timeZone: string | null): string {
  return formatInstantDay(dateStr, timeZone, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-US') ?? '—';
}

/** valid_until is a calendar day; created_at is an instant. An unreadable
 *  day is absent, and absent renders as an em dash — never two hyphens. */
function formatDay(dateStr: string | null | undefined): string {
  return formatCalendarDate(dateStr, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';
}

/**
 * A sortable heading cell in the handoff palette.
 *
 * It is deliberately LOCAL. `SortTh` is the shared one and 20-odd screens still
 * use it, but its skin is spelled in the older `--fieldiq-*` names, and this
 * page is the first thing in the app to speak `--fi-*`. Re-skinning the shared
 * component would repaint every one of those screens; passing overrides through
 * its `className` would decide the colour by stylesheet order, which is not
 * something a caller gets to control.
 *
 * The SORT LOGIC is not duplicated — `sortCompare` and `makeToggleSort` are
 * imported from that same module, so there is one comparator, not two. Only the
 * paint is local, and only until a second re-skinned page needs it; a shared
 * `--fi-*` heading built from one call site is an abstraction guessed rather
 * than observed.
 *
 * Interaction is byte-for-byte what `SortTh` does: the whole heading cell is the
 * click target. `aria-sort` is new and announces the state to a screen reader;
 * it changes no pixels and no behaviour.
 */
interface SortHeadProps {
  label: string;
  field: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  className?: string;
}

function SortHead({ label, field, sortBy, sortDir, onSort, className = '' }: SortHeadProps) {
  const active = sortBy === field;
  return (
    <th
      scope="col"
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort(field)}
      className={`group/th cursor-pointer select-none whitespace-nowrap px-4 py-3 text-left transition hover:text-white ${
        active ? 'text-white' : 'text-fieldiq-muted'
      } ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="inline-flex flex-col leading-none">
          <ChevronUp
            className={`size-3 ${
              active && sortDir === 'asc' ? 'text-fi-amber' : 'text-fieldiq-muted/40 group-hover/th:text-fieldiq-muted/60'
            }`}
          />
          <ChevronDown
            className={`-mt-0.5 size-3 ${
              active && sortDir === 'desc' ? 'text-fi-amber' : 'text-fieldiq-muted/40 group-hover/th:text-fieldiq-muted/60'
            }`}
          />
        </span>
      </span>
    </th>
  );
}

export default function QuotesScreen() {
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  // Sales reads quotes and writes none (api#1032): no New Quote, and the
  // dashboard figures (refused to sales) are not fetched, so the stat strip and
  // chip counts render as gaps rather than as zeros.
  const { user, hasVertical } = useAuth();
  const isSales = isSalesRole(user?.role);
  // The tenant-wide figure's currency: CA$ / US$ for an NDT tenant, null (the bare '$') otherwise.
  const tenantQuoteCurrency = ndtTenantQuoteCurrency(hasVertical, useTenantCurrency());
  const [status, setStatus] = useState('all');
  const [customerId, setCustomerId] = useState('');

  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['quote-dashboard'],
    enabled: !isSales,
    queryFn: async () => {
      const res = await api.quotes.dashboard();
      return unwrap<Record<string, any>>(res.data);
    },
    retry: false,
  });

  // A failed list is not an empty pipeline. Before fe#573 this query was
  // destructured `{ data, isLoading }`, so a dead API left `quotesData`
  // undefined, `quotes` fell to `[]`, and the register rendered "No quotes
  // found. Create your first quote to get started" -- a read failure wearing
  // the costume of a fresh tenant, offering a WRITE as the remedy. The error
  // is carried out to the register's third branch below.
  const quotesQuery = useQuery({
    queryKey: ['quotes', status, customerId],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (status !== 'all') params.status = status;
      if (customerId) params.customerId = customerId;
      const res = await api.quotes.list(params);
      return unwrapArray<any>(res.data);
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  // `queryLoadFailure`, not `.error`: a query waiting to retry reports
  // `error: null` and keeps the failure on `failureReason`.
  const { data: quotesData, isLoading: quotesLoading } = quotesQuery;
  const quotesFailure = queryLoadFailure(quotesQuery);

  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const res = await api.customers.list();
      return unwrapArray<any>(res.data);
    },
    retry: false,
  });

  const quotes = quotesData || [];
  const customers = customersData || [];
  const dashboard = dashboardData || {};
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = makeToggleSort(setSortBy, setSortDir, sortBy);

  const sortedQuotes = useMemo(() => {
    return [...quotes].sort((a: any, b: any) => {
      const aVal = a[sortBy] ?? '';
      const bVal = b[sortBy] ?? '';
      return sortCompare(aVal, bVal, sortDir);
    });
  }, [quotes, sortBy, sortDir]);

  /** Enter and Space activate a row, matching the handoff ledgers. */
  const openQuote = (id: string) => navigate(`/quotes/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openQuote(id);
    }
  };

  return (
    <div data-testid="quotes-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell above this renders the one branded bar; pages never do. */}
      <PageHeaderBand
        eyebrow="Commercial pipeline"
        title="Quotes"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          isSales ? undefined : <button
            type="button"
            onClick={() => navigate('/quotes/create')}
            className="fi-action fi-action--standard fi-action--primary"
          >
            <Plus className="size-4" aria-hidden="true" />
            New Quote
          </button>
        }
        /* The four figures the dashboard endpoint returns, unchanged: the same
           query, the same keys, and displayValue() still renders an absent one
           as a gap. The sub-lines are static copy describing what each figure
           counts — `pending_value` is SUM(total_amount) FILTER (status='sent')
           in quotes.routes.ts — and none of them is computed here. They are
           company-wide and deliberately do NOT follow the tabs below. */
        stats={[
          {
            icon: <FileText className="size-4" aria-hidden="true" />,
            label: 'Drafts',
            sub: 'Not yet sent',
            tone: 'neutral',
            loading: dashboardLoading,
            value: displayValue(dashboard.draft_count),
          },
          {
            icon: <Send className="size-4" aria-hidden="true" />,
            label: 'Sent',
            sub: 'Awaiting a decision',
            tone: 'info',
            loading: dashboardLoading,
            value: displayValue(dashboard.sent_count),
          },
          {
            icon: <CheckCircle className="size-4" aria-hidden="true" />,
            label: 'Accepted',
            sub: 'Won',
            tone: 'success',
            loading: dashboardLoading,
            value: displayValue(dashboard.accepted_count),
          },
          {
            icon: <DollarSign className="size-4" aria-hidden="true" />,
            label: 'Pending Value',
            sub: 'Sum of sent quotes',
            tone: 'accent',
            loading: dashboardLoading,
            value: formatMoney(dashboard.pending_value, tenantQuoteCurrency),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* The lifecycle strip: STATIC COPY, and deliberately not a stepper.
              It names the stages a quote passes through so the register below
              has a frame; it does not know which stage any quote is at, has no
              state, and reads nothing. A version that highlighted the current
              stage would be a second answer to the question the status column
              already answers. */}
          <div
            data-testid="quote-lifecycle"
            className="flex flex-col gap-1 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-2.5 sm:flex-row sm:items-baseline sm:gap-3"
          >
            <p className="shrink-0 text-[0.6rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
              Quote lifecycle
            </p>
            <p className="text-xs leading-5 text-fieldiq-text">
              Price source → internal review → customer decision → job / invoice context
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
              {STATUS_TABS.map((tab) => {
                const active = status === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setStatus(tab.key)}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active
                        ? 'bg-fi-amber text-fi-amber-ink'
                        : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                    }`}
                  >
                    {tab.label}
                    {/* Absent is a gap, never a zero — `Expired 0` is a real
                        count and has to keep reading as one, so the two cannot
                        share a spelling. While the dashboard is in flight
                        there is no count to show and none is claimed; the stat
                        strip above masks its figures over the same window. */}
                    {!dashboardLoading && (
                      <span
                        className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}
                      >
                        {displayValue(dashboard[tab.countKey])}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="relative flex items-center gap-2">
              <Filter className="size-3.5 text-fieldiq-muted" aria-hidden="true" />
              <select
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                aria-label="Filter by client"
                className="min-w-[160px] appearance-none rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 pr-8 text-sm text-white focus:border-fi-amber focus:outline-none"
              >
                <option value="">All Clients</option>
                {customers.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <section
            data-testid="quote-register"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Quote register</p>
              <h2 className="mt-1 text-base font-extrabold tracking-tight text-white">Customer offers</h2>
            </header>
            {quotesLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" />
              </div>
            ) : quotesFailure ? (
              <LoadFailureNotice
                error={queryFailureError(quotesQuery)}
                kind={quotesFailure}
                subject="quotes"
                allowed="Office roles"
              />
            ) : quotes.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <FileText className="mx-auto mb-3 size-10 text-fieldiq-muted" aria-hidden="true" />
                <p className="text-sm text-fieldiq-text">No quotes found.</p>
                <p className="mt-1 text-xs text-fieldiq-muted">Create your first quote to get started.</p>
              </div>
            ) : (
              <>
                {/* Wide viewports: the register as a tabular listing. */}
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[860px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em]">
                      {/* The heading row carries the same 4px on its left
                          edge in no colour at all. Without it the head sits
                          4px left of every cell under it, and a column that
                          does not line up with its own values reads as a
                          rendering bug. */}
                      <tr className="border-l-4 border-l-transparent">
                        <SortHead label="Quote #" field="quote_number" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortHead label="Client" field="customer_name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortHead label="Created" field="created_at" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortHead label="Valid Until" field="valid_until" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortHead label="Amount" field="total_amount" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="!text-right" />
                        <SortHead label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <th scope="col" className="w-[70px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedQuotes.map((quote: any) => (
                        <tr
                          key={quote.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => openQuote(quote.id)}
                          onKeyDown={rowKeys(quote.id)}
                          /* The status rail lives on the ROW, which is also
                             the only place it can live: the panel around this
                             table already draws one amber edge for the whole
                             register, and a second rail nested inside a row
                             would be a third vertical line saying nothing. */
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${
                            STATUS_RAIL[quote.status] ?? 'border-l-transparent'
                          }`}
                        >
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-extrabold text-fi-amber">{quote.quote_number}</td>
                          <td className="px-4 py-3 text-sm font-bold text-white">
                            {quote.customer_name || quote.customer_display_name || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-muted">
                            {quote.created_at ? formatDate(quote.created_at, timeZone) : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-muted">
                            {quote.valid_until ? formatDay(quote.valid_until) : '—'}
                          </td>
                          <td
                            className={`px-4 py-3 text-right text-sm font-extrabold ${
                              quote.status === 'accepted'
                                ? 'text-fi-green'
                                : quote.status === 'declined'
                                  ? 'text-fi-red'
                                  : 'text-white'
                            }`}
                          >
                            {formatMoney(quote.total_amount, ndtQuoteCurrency(quote))}
                          </td>
                          <td className="px-4 py-3">
                            <QuoteStatusBadge status={QUOTE_STATUS[quoteListStatus(quote)] || QUOTE_STATUS.draft} />
                          </td>
                          <td className="px-4 py-3">
                            <ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Narrow viewports: the same rows, stacked. No amber rail here —
                    that edge belongs to the surrounding panel, never to a row. */}
                <div className="space-y-3 p-4 md:hidden">
                  {sortedQuotes.map((quote: any) => (
                    <div
                      key={quote.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openQuote(quote.id)}
                      onKeyDown={rowKeys(quote.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <div className="px-5 pb-3 pt-4">
                        <div className="mb-2 flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <span className="grid size-8 place-items-center rounded-fi-control bg-fieldiq-dark">
                              <FileText className="size-3.5 text-fieldiq-muted" aria-hidden="true" />
                            </span>
                            <span className="text-sm font-extrabold text-fi-amber">{quote.quote_number}</span>
                          </div>
                          <QuoteStatusBadge status={QUOTE_STATUS[quoteListStatus(quote)] || QUOTE_STATUS.draft} />
                        </div>
                        <h3 className="font-display text-base font-extrabold tracking-tight text-white">
                          {quote.customer_name || quote.customer_display_name || '—'}
                        </h3>
                      </div>
                      <div className="flex items-center justify-between gap-3 border-t border-fieldiq-border px-5 py-3">
                        <span className="flex items-center gap-1.5 text-sm text-fieldiq-text">
                          <Clock className="size-3.5" aria-hidden="true" />
                          <span>
                            {quote.valid_until
                              ? `Valid until ${formatDay(quote.valid_until)}`
                              : quote.created_at
                                ? formatDate(quote.created_at, timeZone)
                                : '—'}
                          </span>
                        </span>
                        <span
                          className={`text-base font-extrabold ${
                            quote.status === 'accepted'
                              ? 'text-fi-green'
                              : quote.status === 'declined'
                                ? 'text-fi-red'
                                : 'text-fi-amber'
                          }`}
                        >
                          {formatMoney(quote.total_amount, ndtQuoteCurrency(quote))}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </section>

      {/* The one-touch create. `right-4`, the same as Jobs and Tickets: the
          `right-28` this carried was measured to clear a viewport-pinned agent
          button that no longer exists (fe#680), and nothing occupies that
          strip now. */}
      <button
        type="button"
        onClick={() => navigate('/quotes/create')}
        aria-label="New quote"
        className="fixed bottom-[var(--fab-bottom)] right-4 z-40 grid size-14 place-items-center rounded-fi-pill bg-fi-amber text-fi-amber-ink shadow-lg transition active:scale-95 hover:brightness-105"
      >
        <Plus className="size-6" aria-hidden="true" />
      </button>
    </div>
  );
}
